import { Injectable } from '@angular/core';
import * as store from '@daas-consulting/core-frontend-store';
import * as models from '@daas-consulting/production/remitos/frontend/models';
import { loader } from '@daas-consulting/production/shared/frontend/store';
import { Selector } from '@ngxs/store';
import { RemitosFormState } from './remitos-form.state';
import { RemitosFormStateModel } from './remitos-form.state-model';

@Injectable()
export class RemitosFormSelectors {
  @Selector([RemitosFormState])
  static formStatus(state: RemitosFormStateModel): string {
    return state.dataForm.status;
  }

  @Selector([RemitosFormState])
  static availablePrescriptions(
    state: RemitosFormStateModel
  ): models.AvailablePrescriptionModel[] {
    return state.availablePrescriptions;
  }

  @Selector([RemitosFormState])
  static institutions(
    state: RemitosFormStateModel
  ): models.InstitutionFormData[] {
    return state.institutions;
  }

  @Selector([RemitosFormState])
  static lines(state: RemitosFormStateModel): models.LineFormData[] {
    return state.lines;
  }

  @Selector([RemitosFormState])
  static articles(state: RemitosFormStateModel): models.ArticleFormData[] {
    return state.articles;
  }

  @Selector([RemitosFormState])
  static activePurchaseOrders(
    state: RemitosFormStateModel
  ): models.PurchaseOrderFormData[] {
    return state.activePurchaseOrders;
  }

  @Selector([RemitosFormState])
  static selectedInstitution(
    state: RemitosFormStateModel
  ): models.InstitutionFormData | null {
    return state.selectedInstitution;
  }

  @Selector([RemitosFormState])
  static selectedPurchaseOrder(
    state: RemitosFormStateModel
  ): models.PurchaseOrderFormData | null {
    return state.selectedPurchaseOrder;
  }

  @Selector([RemitosFormState])
  static additionalArticles(state: RemitosFormStateModel) {
    return state.additionalArticles;
  }

  @Selector([RemitosFormState])
  static selectedLineId(state: RemitosFormStateModel): string | null {
    return state.selectedLineId;
  }

  @Selector([RemitosFormState])
  static selectedLine(
    state: RemitosFormStateModel
  ): models.LineFormData | null {
    if (!state.selectedLineId) {
      return null;
    }
    return state.lines.find((l) => l.lineId === state.selectedLineId) || null;
  }

  @Selector([RemitosFormState])
  static selectedPatientName(state: RemitosFormStateModel): string | null {
    return state.selectedPatientName;
  }

  @Selector([RemitosFormState])
  static lineSummaries(
    state: RemitosFormStateModel
  ): models.LineSummaryModel[] {
    const purchaseOrder = state.selectedPurchaseOrder;
    if (!purchaseOrder?.items?.length) {
      return [];
    }

    let prescriptions = state.availablePrescriptions;
    if (
      state.selectedInstitution?.remitoGroupingRule === 'PER_PATIENT' &&
      state.selectedPatientName
    ) {
      prescriptions = prescriptions.filter(
        (p) => p.patientName === state.selectedPatientName
      );
    }

    if (prescriptions.length === 0) {
      return [];
    }

    const lineMap = new Map(state.lines.map((l) => [l.lineId, l]));
    const rv: models.LineSummaryModel[] = [];
    for (const item of purchaseOrder.items) {
      const line = lineMap.get(item.lineId);
      if (!line) {
        continue;
      }

      rv.push({
        lineId: line.lineId,
        code: line.code,
        description: line.description,
        volumeMin: line.volumeMin,
        volumeMax: line.volumeMax,
        availableCount: prescriptions.length,
        stockRemaining:
          item.stockTotal === null
            ? null
            : item.stockTotal - item.stockConsumed,
      });
    }

    return rv.sort((a, b) => a.code.trim().localeCompare(b.code.trim()));
  }

  @Selector([RemitosFormState])
  static patientSummaries(
    state: RemitosFormStateModel
  ): models.PatientSummaryModel[] {
    if (state.selectedInstitution?.remitoGroupingRule !== 'PER_PATIENT') {
      return [];
    }
    const counts = new Map<string, number>();
    for (const p of state.availablePrescriptions) {
      counts.set(p.patientName, (counts.get(p.patientName) || 0) + 1);
    }
    const rv: models.PatientSummaryModel[] = [];
    for (const [patientName, count] of counts) {
      rv.push({ patientName, availableCount: count });
    }
    return rv.sort((a, b) => a.patientName.localeCompare(b.patientName));
  }

  @Selector([RemitosFormState])
  static filteredAvailablePrescriptions(
    state: RemitosFormStateModel
  ): models.AvailablePrescriptionModel[] {
    if (!state.selectedLineId) {
      return [];
    }
    let prescriptions = state.availablePrescriptions;
    if (
      state.selectedInstitution?.remitoGroupingRule === 'PER_PATIENT' &&
      state.selectedPatientName
    ) {
      prescriptions = prescriptions.filter(
        (p) => p.patientName === state.selectedPatientName
      );
    }
    return prescriptions;
  }

  @Selector([RemitosFormSelectors.filteredAvailablePrescriptions])
  static hasSelectedPrescriptions(
    prescriptions: models.AvailablePrescriptionModel[]
  ): boolean {
    return prescriptions.some((p) => p.selected);
  }

  @Selector([
    RemitosFormSelectors.formStatus,
    loader.selectors.LoaderSelectors.isLoading,
    RemitosFormSelectors.filteredAvailablePrescriptions,
    RemitosFormSelectors.selectedInstitution,
  ])
  static isSubmitDisabled(
    status: string,
    loading: boolean,
    prescriptions: models.AvailablePrescriptionModel[],
    institution: models.InstitutionFormData | null
  ): boolean {
    const selected = prescriptions.filter((p) => p.selected);
    const hasSelected = selected.length > 0;

    if (status === store.FormStatusTypes.INVALID || loading || !hasSelected) {
      return true;
    }

    // Grouping rule validations
    if (institution?.remitoGroupingRule === 'PER_PATIENT') {
      const patients = new Set(selected.map((p) => p.patientName));
      if (patients.size > 1) {
        return true;
      }
    }

    if (institution?.remitoGroupingRule === 'PER_BAG') {
      if (selected.length > 1) {
        return true;
      }
    }

    return false;
  }
}
